import { WorldMap } from "components/organisms";
import { Bubbles } from "components/molecules";
import { BackButton, Card, Content, Page, TopBar } from "components/templates";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import { countryHref, learnHref, topicHref } from "utils/learnPaths";
import countriesData from "utils/countriesData.json";
import { findLearnRoute } from "utils/learnRoutes";
import { findLearnTopic } from "utils/learnTopics";
import { AvailableLanguages, Country } from "utils/types";

const ACCENT = "#f5b944";
const ACCENT_DIM = "#8a6a2f";

const PageTitle = styled.h1`
  margin: 0 0 4px;
  font-family: "Gaegu", cursive;
  font-size: clamp(2rem, 5vw, 2.6rem);
  line-height: 1.05;
  color: #f5e2c8;
  text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35);
`;

const Subtitle = styled.p`
  margin: 0 0 16px;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  letter-spacing: 0.2px;
  color: rgba(173, 216, 230, 0.95);
`;

const Intro = styled.p`
  margin: 0 0 18px;
  max-width: 90%;
  font-family: "Gaegu", cursive;
  font-size: 1.3rem;
  line-height: 1.4;
  color: rgba(245, 226, 200, 0.95);
`;

// The map is the spine of a route: it shows the whole itinerary at once and
// which stop you're reading, which is the thing a plain article can't do.
const MapPanel = styled.div`
  position: relative;
  height: min(46vh, 380px);
  min-height: 240px;
  border-radius: 18px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(27, 38, 79, 0.25);
  overflow: hidden;
  margin-bottom: 18px;
`;

const StopList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 14px;
`;

const StopCard = styled(Card)<{ $active: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 14px;
  align-items: start;
  cursor: pointer;
  border-color: ${({ $active }) =>
    $active ? "rgba(245, 185, 68, 0.75)" : "rgba(245, 226, 200, 0.16)"};
  transition:
    border-color 140ms ease,
    transform 140ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(245, 185, 68, 0.5);
  }
`;

const StopNumber = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  border-radius: 999px;
  font-family: "Finger Paint", cursive;
  font-size: 1rem;
  color: ${({ $active }) => ($active ? "#182848" : "#f5e2c8")};
  background: ${({ $active }) => ($active ? ACCENT : "rgba(24, 40, 72, 0.5)")};
  border: 1px solid rgba(245, 185, 68, 0.55);
`;

const StopRole = styled.p`
  margin: 0 0 2px;
  font-family: "Finger Paint", cursive;
  font-size: 0.72rem;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: rgba(245, 185, 68, 0.9);
`;

const StopTitle = styled.h2`
  margin: 0 0 6px;
  font-family: "Gaegu", cursive;
  font-size: 1.5rem;
  line-height: 1.15;
  color: #f5e2c8;
`;

const StopBody = styled.p`
  margin: 0 0 10px;
  font-family: "Gaegu", cursive;
  font-size: 1.2rem;
  line-height: 1.45;
  color: rgba(245, 226, 200, 0.95);
`;

const CountryLink = styled(Link)`
  display: inline-block;
  color: rgba(173, 216, 230, 0.95);
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const Closing = styled(Card)`
  margin-top: 18px;
  border-color: rgba(245, 185, 68, 0.35);
  background: rgba(245, 185, 68, 0.08);
`;

const ClosingText = styled.p`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;
  line-height: 1.45;
  color: rgba(245, 226, 200, 0.98);
`;

const RelatedSection = styled.div`
  margin-top: 26px;
`;

const RelatedLabel = styled.h2`
  margin: 0 0 10px;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  color: rgba(245, 226, 200, 0.7);
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
`;

const RelatedCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(27, 38, 79, 0.24);
  color: #f5e2c8;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  text-decoration: none;

  &:hover {
    border-color: rgba(173, 216, 230, 0.55);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const ui = {
  en: {
    back: "Back",
    backAria: "Go back",
    allTopics: "All of Learn",
    notFound: "We couldn't find that route.",
    openCountry: (name: string) => `Open ${name} →`,
    related: "Go deeper",
    mapHint: "Tap a stop to follow it on the map.",
  },
  es: {
    back: "Volver",
    backAria: "Volver",
    allTopics: "Todo Aprender",
    notFound: "No encontramos esa ruta.",
    openCountry: (name: string) => `Ver ${name} →`,
    related: "Profundizar",
    mapHint: "Tocá una parada para seguirla en el mapa.",
  },
};

export const LearnRoute = () => {
  const { routeSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useAppLanguage();
  const isSpanish = language === AvailableLanguages.ES;
  const t = isSpanish ? ui.es : ui.en;
  const [activeStop, setActiveStop] = useState(0);

  const route = findLearnRoute(routeSlug);

  // Routes store English country names (stable ids); the UI shows the name in
  // the reading language and links to that page.
  const displayNameByEnglish = useMemo(() => {
    const map = new Map<string, string>();
    (countriesData as Country[]).forEach((country) => {
      map.set(
        country["english-name"],
        isSpanish ? country["spanish-name"] : country["english-name"]
      );
    });
    return map;
  }, [isSpanish]);

  // Declared with the other hooks, before the not-found guard below, so the
  // hook order stays the same on every render.
  const stopDisplayNames = useMemo(
    () =>
      new Set(
        (route?.stops ?? []).map(
          (stop) => displayNameByEnglish.get(stop.country) ?? stop.country
        )
      ),
    [route, displayNameByEnglish]
  );

  const countryColors = useMemo(() => {
    if (!route) return {};
    const colors: Record<string, string> = {};
    route.stops.forEach((stop, index) => {
      colors[stop.country] = index === activeStop ? ACCENT : ACCENT_DIM;
    });
    return colors;
  }, [route, activeStop]);

  if (!route) {
    return (
      <Page id="learn-route" lang={isSpanish ? "es" : "en"}>
        <Bubbles />
        <Content>
          <TopBar>
            <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
              <span aria-hidden="true">←</span> {t.back}
            </BackButton>
          </TopBar>
          <Card>
            <StopBody>{t.notFound}</StopBody>
            <CountryLink to={learnHref(isSpanish)}>{t.allTopics}</CountryLink>
          </Card>
        </Content>
      </Page>
    );
  }

  const locale = isSpanish ? route.es : route.en;
  const relatedTopics = (route.related ?? [])
    .map((slug) => findLearnTopic(slug))
    .filter((topic): topic is NonNullable<typeof topic> => !!topic);

  return (
    <Page id="learn-route" lang={isSpanish ? "es" : "en"}>
      <Bubbles />
      <Content>
        <TopBar>
          <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
            <span aria-hidden="true">←</span> {t.back}
          </BackButton>
        </TopBar>

        <PageTitle>
          <span aria-hidden="true">{route.emoji}</span> {locale.title}
        </PageTitle>
        <Subtitle>{locale.subtitle}</Subtitle>
        <Intro>{locale.intro}</Intro>

        <MapPanel>
          <WorldMap
            data={countriesData}
            language={language ?? AvailableLanguages.EN}
            showControls={false}
            fillHeight
            countryColors={countryColors}
            /* Only the stops carry the route: a country that isn't part of
               this story has no thread to keep, and the parameter would just
               be noise in its URL. */
            countryTo={(displayName) =>
              countryHref(
                isSpanish,
                displayName,
                stopDisplayNames.has(displayName) ? route : undefined
              )
            }
            showHoverInvite={false}
          />
        </MapPanel>
        <Subtitle as="p">{t.mapHint}</Subtitle>

        <StopList>
          {route.stops.map((stop, index) => {
            const stopLocale = isSpanish ? stop.es : stop.en;
            const displayName =
              displayNameByEnglish.get(stop.country) ?? stop.country;

            return (
              <li key={stop.country + index}>
                <StopCard
                  $active={index === activeStop}
                  onMouseEnter={() => setActiveStop(index)}
                  onFocus={() => setActiveStop(index)}
                  onClick={() => setActiveStop(index)}
                  tabIndex={0}
                >
                  <StopNumber $active={index === activeStop} aria-hidden="true">
                    {index + 1}
                  </StopNumber>
                  <div>
                    <StopRole>
                      <span aria-hidden="true">{stop.emoji}</span>{" "}
                      {stopLocale.role}
                    </StopRole>
                    <StopTitle>{stopLocale.title}</StopTitle>
                    <StopBody>{stopLocale.body}</StopBody>
                    <CountryLink to={countryHref(isSpanish, displayName, route)}>
                      {t.openCountry(displayName)}
                    </CountryLink>
                  </div>
                </StopCard>
              </li>
            );
          })}
        </StopList>

        <Closing>
          <ClosingText>{locale.closing}</ClosingText>
        </Closing>

        {relatedTopics.length > 0 && (
          <RelatedSection>
            <RelatedLabel>{t.related}</RelatedLabel>
            <RelatedGrid>
              {relatedTopics.map((topic) => (
                <RelatedCard key={topic.slug} to={topicHref(isSpanish, topic)}>
                  <span aria-hidden="true">{topic.emoji}</span>
                  {isSpanish ? topic.es.title : topic.en.title}
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedSection>
        )}
      </Content>
    </Page>
  );
};
